import { useState, useEffect } from 'react';

import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Loader2, Minus, Plus } from 'lucide-react';

import { useAuth } from '@/contexts/AuthContext';
import { billingApi, TopUpConfigResponse } from '@/lib/api/billing';
import { useEmbedStore } from '@/stores/embed-store';
import toast from 'react-hot-toast';

interface TopUpCreditsModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export function TopUpCreditsModal({ isOpen, onClose }: TopUpCreditsModalProps) {
  const { getIdToken } = useAuth();
  const [loading, setLoading] = useState(false);
  const [configLoading, setConfigLoading] = useState(true);
  const [config, setConfig] = useState<TopUpConfigResponse | null>(null);
  const [credits, setCredits] = useState(50);

  // Load top-up config when modal opens
  useEffect(() => {
    if (isOpen) {
      loadConfig();
    }
  }, [isOpen]);

  const loadConfig = async () => {
    setConfigLoading(true);
    try {
      const data = await billingApi.getTopUpConfig();
      setConfig(data);
      setCredits(data.minCredits);
    } catch (error: any) {
      console.error('Failed to load top-up config:', error);
      toast.error('Failed to load pricing');
    } finally {
      setConfigLoading(false);
    }
  };

  const handleIncrement = () => {
    if (config) {
      setCredits((prev) => prev + config.stepCredits);
    }
  };

  const handleDecrement = () => {
    if (config && credits > config.minCredits) {
      setCredits((prev) => prev - config.stepCredits);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = parseInt(e.target.value, 10);
    if (!isNaN(value) && value > 0 && config) {
      // Round to nearest step
      const rounded = Math.round(value / config.stepCredits) * config.stepCredits;
      setCredits(Math.max(config.minCredits, rounded));
    }
  };

  const calculatePrice = (): number => {
    if (!config) return 0;
    return (credits * config.pricePerCreditCents) / 100;
  };

  const handlePurchase = async () => {
    setLoading(true);
    try {
      const token = await getIdToken();
      if (!token) {
        toast.error('Authentication required');
        return;
      }

      const { url } = await billingApi.topUpCredits(token, credits);

      // In embed mode, open in new tab (can't redirect iframe)
      if (useEmbedStore.getState().isEmbedded) {
        window.open(url, '_blank');
        toast.success('Checkout opened in new tab');
        onClose();
      } else {
        window.location.href = url;
      }
    } catch (error: any) {
      console.error('Failed to create checkout:', error);
      toast.error(error.response?.data?.message || 'Failed to start checkout');
      setLoading(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-[90vw] w-full sm:max-w-[440px] rounded-xl p-0 gap-0" onOpenAutoFocus={(e) => e.preventDefault()}>
        <DialogHeader className="px-6 pt-6 pb-4">
          <DialogTitle className="text-lg">Top Up Credits</DialogTitle>
          <DialogDescription className="text-sm">
            Purchase additional credits for your workspace
          </DialogDescription>
        </DialogHeader>

        {configLoading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
          </div>
        ) : config ? (
          <div className="px-6 pb-6 space-y-5">
            {/* Credit selector */}
            <div className="flex flex-col items-center gap-2 py-3">
              <div className="flex items-center gap-5">
                <button
                  onClick={handleDecrement}
                  disabled={credits <= config.minCredits || loading}
                  className="h-9 w-9 rounded-full border border-border hover:bg-muted flex items-center justify-center text-muted-foreground disabled:opacity-30 transition-colors"
                >
                  <Minus className="h-3.5 w-3.5" />
                </button>
                <span className="text-4xl font-bold tabular-nums min-w-[80px] text-center">
                  {credits}
                </span>
                <button
                  onClick={handleIncrement}
                  disabled={loading}
                  className="h-9 w-9 rounded-full border border-border hover:bg-muted flex items-center justify-center text-muted-foreground disabled:opacity-30 transition-colors"
                >
                  <Plus className="h-3.5 w-3.5" />
                </button>
              </div>
              <p className="text-sm text-muted-foreground">
                credits
              </p>
            </div>

            {/* Info */}
            <p className="text-xs text-muted-foreground text-center">
              Never expires · Used after plan credits run out
            </p>

            {/* Purchase button */}
            <Button
              onClick={handlePurchase}
              disabled={loading}
              className="w-full h-10 text-sm bg-foreground hover:bg-foreground/90 text-background"
            >
              {loading ? (
                <>
                  <Loader2 className="mr-1.5 h-4 w-4 animate-spin" />
                  Processing...
                </>
              ) : (
                `Purchase for $${calculatePrice().toFixed(2)}`
              )}
            </Button>
          </div>
        ) : (
          <div className="py-12 text-center text-sm text-muted-foreground">
            Unable to load pricing. Please try again.
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
